import { Component, effect, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IceBackButtonComponent } from '@app/shared/components/atoms/ice-back-button/ice-back-button.component';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { AdditionalServiceComponent } from '@app/sales/modules/subscription/components/subscription-stepper/additional-services-step/additional-service/additional-service.component';
import { MutexAdditionalServiceComponent } from '@app/sales/modules/subscription/components/subscription-stepper/additional-services-step/mutex-additional-service/mutex-additional-service.component';
import {
  AdditionalServiceMutex,
  AdditionalServiceVoice,
  ServiceStatus,
} from '@app/sales/modules/subscription/models/additional-service-voice.model';
import { merge, Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  AddAdditionalServiceToExistingSubscriptionAction,
  RemoveAdditionalServiceFromExistingSubscriptionAction,
  UpdateAdditionalServiceToExistingSubscriptionAction,
} from '@app/sales/states/customer-product/customer-product.actions';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { deepEqual } from '@app/core/utils';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IconNames } from '@app/shared/enums/icon-names';
import { SubscriptionHelperService } from '@sales/services/subscription-helper.service';
import { SimCardDelivery } from '@sales/models/simcard.model';
import { PcmProduct, PcmProductCategoryType } from '@app/sales/modules/subscription/models/pcm-product.model';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import {
  getExistingValueAddedServiceExplanation,
  getUniqueSellingPointsTitle,
} from '@app/sales/modules/subscription/constants/value-added-service-product';
import { IcePillComponent } from '@app/shared/components/atoms/ice-pill/ice-pill.component';
import { PriceFormatPipe, PriceType } from '@app/shared/pipes/price-format.pipe';
import { IceInfoBoxComponent } from '@app/shared/components/atoms/ice-info-box/ice-info-box.component';
import { FeatureGateService } from '@app/shared/services/featuregate.service';
import {
  ActiveSim,
  AdditionalSimGroupComponent,
  SimRow,
} from '@app/sales/modules/subscription/components/additional-sim-group/additional-sim-group.component';
import { MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER } from '@sales/models/simcard.model';

interface SimGroup {
  key: string;
  service: AdditionalServiceVoice;
  activeSims: ActiveSim[];
}

const simTypeKey = (service: AdditionalServiceVoice): string => (service.isDataSimCard ? 'data' : 'twin');

@Component({
  selector: 'app-change-additional-services',
  imports: [
    IceBackButtonComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    AdditionalServiceComponent,
    MutexAdditionalServiceComponent,
    IceButtonComponent,
    IceAlertComponent,
    IcePillComponent,
    PriceFormatPipe,
    IceInfoBoxComponent,
    AdditionalSimGroupComponent,
  ],
  templateUrl: './change-additional-services.component.html',
  styleUrl: './change-additional-services.component.scss',
})
export class ChangeAdditionalServices implements OnInit, OnDestroy {
  store = inject(Store);
  private featureGateService = inject(FeatureGateService);

  product = input<VoiceProduct>();
  isLoadingAdditionalServiceOfferings = input<boolean>();
  finishedChanging = output<void>();

  form = new FormArray([]);
  simForm = new FormArray<FormGroup>([]);
  simGroups = signal<SimGroup[]>([]);
  simRows = signal<Record<string, SimRow[]>>({});
  private nextSimRowId = 1;
  multipleExtraSims = this.featureGateService.isFeatureEnabled('featureMultipleExtraSims');
  subscriptions$: Subscription[] = [];

  formHasChanges = false;

  IconNames = IconNames;
  getUniqueSellingPointsTitle = getUniqueSellingPointsTitle;
  PriceType = PriceType;
  getExistingValueAddedServiceExplanation = getExistingValueAddedServiceExplanation;

  constructor() {
    effect(() => {
      if (this.form.controls.length === 0 && this.product()?.subscriptionServices) {
        this.buildForm();
      }
    });
  }

  ngOnInit(): void {
    this.buildForm();

    const initialValues = this.form.getRawValue();
    const initialSimValues = this.simForm.getRawValue();
    this.subscriptions$.push(
      merge(this.form.valueChanges, this.simForm.valueChanges).subscribe(() => {
        const isDirty =
          !deepEqual(initialValues, this.form.getRawValue()) || !deepEqual(initialSimValues, this.simForm.getRawValue());
        this.formHasChanges = isDirty;
      })
    );
  }

  private buildForm() {
    if (!this.product().subscriptionServices) return;

    if (this.multipleExtraSims && this.simGroups().length === 0) {
      this.buildSimGroups();
    }

    const availableAdditionalServices = this.product().subscriptionServices.serviceProducts.filter(
      service => !this.isGroupedSim(service)
    );
    const availableMutexServices = this.product().subscriptionServices.mutexServiceSets;

    availableAdditionalServices.forEach(service => {
      const group = this.getAdditionalServiceFormGroup(service);
      this.form.push(group);
    });

    availableMutexServices.forEach(set => {
      const group = this.getMutexServiceFormGroup(set);
      this.form.push(group);
    });
  }

  private getAdditionalServiceFormGroup(service: AdditionalServiceVoice): FormGroup {
    const existingAdditionalServices = this.product().additionalServices;
    const existing = existingAdditionalServices.find(s => s.productId === service.productId);
    const isSelected = !!existing;

    const requiresIcc = service.isDataSimCard || service.isTwinSimCard;

    const groupConfig: Record<string, FormControl> = {
      productId: new FormControl(service.productId),
      selected: new FormControl(isSelected),
    };

    if (requiresIcc) {
      const icc = service.icc || existing?.icc || '';
      const orderSim = service.orderSim || (existing?.orderSim ?? false);
      const validators = service.requireICC ? [Validators.required] : [];
      groupConfig.icc = new FormControl(icc, validators);
      groupConfig.simDelivery = new FormControl(SubscriptionHelperService.getSimCardDelivery(icc, orderSim), validators);
    }

    if (!!service.icc) {
      groupConfig.icc.disable();
      groupConfig.simDelivery.disable();
    }
    const group = new FormGroup(groupConfig);

    if (requiresIcc && !service.icc) {
      const iccControl = group.get('icc') as FormControl;
      const simDeliveryControl = group.get('simDelivery') as FormControl;
      const selectedControl = group.get('selected') as FormControl;

      const syncEnabledState = () => this.syncSimControls(selectedControl, simDeliveryControl, iccControl);
      syncEnabledState();

      this.subscriptions$.push(
        selectedControl.valueChanges.subscribe(syncEnabledState),
        simDeliveryControl.valueChanges.subscribe(syncEnabledState)
      );
    }
    return group;
  }

  private isGroupedSim(service: AdditionalServiceVoice): boolean {
    return this.multipleExtraSims && SubscriptionHelperService.isAdditionalSim(service);
  }

  private syncSimControls(selectedControl: FormControl, simDeliveryControl: FormControl, iccControl: FormControl) {
    const selected = !!selectedControl.value;
    const inStore = simDeliveryControl.value === SimCardDelivery.InStore;

    if (selected) {
      simDeliveryControl.enable({ emitEvent: false });
    } else {
      simDeliveryControl.disable({ emitEvent: false });
    }

    if (!inStore) {
      iccControl.reset('', { emitEvent: false });
    }
    if (selected && inStore) {
      iccControl.enable({ emitEvent: false });
    } else {
      iccControl.disable({ emitEvent: false });
    }
  }

  private buildSimGroups() {
    const product = this.product();
    const sims = product.subscriptionServices.serviceProducts.filter(SubscriptionHelperService.isAdditionalSim);
    const keys = [...new Set(sims.map(simTypeKey))];

    const groups = keys.map(key => {
      const ofType = sims.filter(service => simTypeKey(service) === key);
      const activeSims = ofType
        .filter(SubscriptionHelperService.isServiceActive)
        .map((service, index) => this.attachActiveSim(service, `${service.description} ${index + 1}`));
      const purchasable =
        ofType.find(service => !SubscriptionHelperService.isServiceActive(service)) ??
        this.purchasableFrom(activeSims[0].service);
      return { key, service: purchasable, activeSims };
    });

    const addedNow = product.additionalServices.filter(
      service => SubscriptionHelperService.isAdditionalSim(service) && !SubscriptionHelperService.isServiceActive(service)
    );
    this.simGroups.set(groups);
    this.simRows.set(
      Object.fromEntries(
        groups.map(group => [
          group.key,
          addedNow
            .filter(service => simTypeKey(service) === group.key)
            .map(service => this.attachSimRow(service.instanceId, service.icc, service.orderSim)),
        ])
      )
    );
  }

  // CDS leaves out the product's inactive row once a SIM of it is active
  private purchasableFrom(active: AdditionalServiceVoice): AdditionalServiceVoice {
    return {
      ...active,
      instanceId: undefined,
      icc: '',
      aNumber: null,
      orderSim: false,
      serviceStatus: ServiceStatus.Inactive,
      startDate: null,
      endDate: null,
    };
  }

  private attachActiveSim(service: AdditionalServiceVoice, label: string): ActiveSim {
    const isKept = this.product().additionalServices.some(s => SubscriptionHelperService.isSameInstance(s, service));
    const form = this.simFormGroup(service.instanceId, isKept, service.icc, service.orderSim);
    form.controls.icc.disable();
    form.controls.simDelivery.disable();
    if (service.aNumber && service.aNumber === this.product().replaceAdditionalSimANumber) {
      form.controls.selected.disable();
    }
    this.simForm.push(form);
    return { service, label, form };
  }

  private attachSimRow(instanceId: string, icc = '', orderSim = false): SimRow {
    const form = this.simFormGroup(instanceId, true, icc, orderSim);
    const sync = () =>
      this.syncSimControls(
        form.controls.selected as FormControl,
        form.controls.simDelivery as FormControl,
        form.controls.icc as FormControl
      );
    sync();
    this.subscriptions$.push(
      form.controls.selected.valueChanges.subscribe(sync),
      form.controls.simDelivery.valueChanges.subscribe(sync)
    );
    this.simForm.push(form);
    return { instanceId, form };
  }

  private simFormGroup(instanceId: string, selected: boolean, icc: string, orderSim: boolean): FormGroup {
    return new FormGroup({
      instanceId: new FormControl(instanceId),
      selected: new FormControl(selected),
      icc: new FormControl(icc ?? '', Validators.required),
      simDelivery: new FormControl(SubscriptionHelperService.getSimCardDelivery(icc, orderSim), Validators.required),
    });
  }

  addSimRow(group: SimGroup) {
    const rows = this.simRows()[group.key];
    if (rows.length >= Math.min(group.service.availableQuantity ?? 1, MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER)) {
      return;
    }
    const row = this.attachSimRow(`new-${this.nextSimRowId++}`);
    this.simRows.update(all => ({ ...all, [group.key]: [...rows, row] }));
  }

  removeSimRow(group: SimGroup, row: SimRow) {
    this.simForm.removeAt(this.simForm.controls.indexOf(row.form));
    this.simRows.update(all => ({ ...all, [group.key]: all[group.key].filter(r => r !== row) }));
  }

  clearSimRows(group: SimGroup) {
    this.simRows()[group.key].forEach(row => this.removeSimRow(group, row));
  }

  private saveSimChanges() {
    const product = this.product();

    this.simGroups()
      .flatMap(group => group.activeSims)
      .forEach(({ service, form }) => {
        const isKept = product.additionalServices.some(s => SubscriptionHelperService.isSameInstance(s, service));
        const isSelected = !!form.controls.selected.value;
        if (isKept && !isSelected) {
          this.store.dispatch(new RemoveAdditionalServiceFromExistingSubscriptionAction(product.id, service));
        } else if (!isKept && isSelected) {
          this.store.dispatch(new AddAdditionalServiceToExistingSubscriptionAction(product.id, service, false));
        }
      });

    const selectedRows = this.simGroups().flatMap(group =>
      this.simRows()
        [group.key].filter(row => row.form.controls.selected.value)
        .map(row => ({ row, purchasable: group.service }))
    );
    const storedRows = product.additionalServices.filter(
      service => SubscriptionHelperService.isAdditionalSim(service) && !SubscriptionHelperService.isServiceActive(service)
    );

    storedRows
      .filter(stored => !selectedRows.some(({ row }) => row.instanceId === stored.instanceId))
      .forEach(stored => this.store.dispatch(new RemoveAdditionalServiceFromExistingSubscriptionAction(product.id, stored)));

    selectedRows.forEach(({ row, purchasable }) => {
      const icc = row.form.controls.icc.value || '';
      const orderSim = row.form.controls.simDelivery.value === SimCardDelivery.ByMail;
      const stored = storedRows.find(s => s.instanceId === row.instanceId);

      if (!stored) {
        this.store.dispatch(
          new AddAdditionalServiceToExistingSubscriptionAction(
            product.id,
            { ...purchasable, instanceId: undefined, icc, orderSim },
            false
          )
        );
      } else if (stored.icc !== icc || (stored.orderSim ?? false) !== orderSim) {
        this.store.dispatch(new UpdateAdditionalServiceToExistingSubscriptionAction(product.id, stored, icc, orderSim));
      }
    });
  }

  private getMutexServiceFormGroup(set: AdditionalServiceMutex): FormGroup {
    const existingAdditionalServices = this.product().additionalServices;
    const isMutexServiceSelected = existingAdditionalServices.some(
      existingService => existingService.mutexSetName === set.name
    );

    return new FormGroup({
      mutexName: new FormControl(set.name),
      isMutexService: new FormControl(true),
      selected: new FormControl(isMutexServiceSelected),
      selectedMutexService: new FormControl(
        isMutexServiceSelected ? existingAdditionalServices.find(s => s.mutexSetName === set.name)?.productId || null : null
      ),
    });
  }

  onSave() {
    const addedServices = this.getAddedServices(this.product());
    const removedServices = this.getRemovedServices(this.product());

    addedServices.forEach(service => {
      this.store.dispatch(
        new AddAdditionalServiceToExistingSubscriptionAction(this.product().id, service, !!service.mutexSetName)
      );
    });
    removedServices.forEach((service: AdditionalServiceVoice) => {
      this.store.dispatch(new RemoveAdditionalServiceFromExistingSubscriptionAction(this.product().id, service));
    });
    if (this.multipleExtraSims) {
      this.saveSimChanges();
    }

    this.handleAdditionalServicesUpdate();

    this.formHasChanges = false;
    this.finishedChanging.emit();
  }

  private handleAdditionalServicesUpdate() {
    const productValue = this.product();

    this.form.controls
      .filter((group: FormGroup) => !group.controls.isMutexService) // regular services
      .forEach((group: FormGroup) => {
        const productId = group.controls.productId.value;
        const existing = productValue.additionalServices.find(s => s.productId === productId);
        if (!existing) return; // was added now or not selected

        const isHandledByRemoval = !group.controls.selected.value;
        if (isHandledByRemoval) return;

        const newIcc = group.get('icc')?.getRawValue();
        const newOrderSim = group.get('simDelivery')?.value === SimCardDelivery.ByMail;

        const isIccChanged = existing.icc !== newIcc;
        const isOrderSimChanged = (existing.orderSim ?? false) !== newOrderSim;

        if (isIccChanged || isOrderSimChanged) {
          this.store.dispatch(
            new UpdateAdditionalServiceToExistingSubscriptionAction(productValue.id, existing, newIcc || '', newOrderSim)
          );
        }
      });
  }

  getAddedServices(product: VoiceProduct): AdditionalServiceVoice[] {
    const addedServices: AdditionalServiceVoice[] = this.getAddedAdditionalServices(product);
    const addedMutexServices: AdditionalServiceVoice[] = this.getAddedMutexServices(product);

    return addedServices.concat(addedMutexServices);
  }

  private getAddedMutexServices(product: VoiceProduct): AdditionalServiceVoice[] {
    return product.subscriptionServices.mutexServiceSets.flatMap(set => {
      const control = this.form.controls
        .filter((x: FormGroup) => x.controls.isMutexService)
        .find((x: FormGroup) => x.controls.mutexName.value === set.name);

      return set.mutexServices
        .map(service => {
          const isServiceSelected = product.additionalServices.find(s => s.productId === service.productId);

          if (
            (control as FormGroup)?.controls.selected.value &&
            (control as FormGroup).controls.selectedMutexService.value === service.productId &&
            !isServiceSelected
          ) {
            return { ...service, mutexSetName: set.name };
          }
          return null;
        })
        .filter((service): service is AdditionalServiceVoice => service !== null);
    });
  }

  private getAddedAdditionalServices(product: VoiceProduct): AdditionalServiceVoice[] {
    return product.subscriptionServices.serviceProducts
      .filter(service => !this.isGroupedSim(service))
      .map(service => {
        const productContainsService = product.additionalServices.some(s => s.productId === service.productId);

        const control = this.form.controls
          .filter((x: FormGroup) => !x.controls.isMutexService)
          .find((x: FormGroup) => x.controls.productId.value === service.productId);

        const isSelectedInForm = (control as FormGroup)?.controls.selected.value;
        if (isSelectedInForm && !productContainsService) {
          return {
            ...service,
            ...(service.requireICC && {
              orderSim: (control as FormGroup).controls.simDelivery?.value === SimCardDelivery.ByMail,
              icc: (control as FormGroup).controls.icc?.value,
            }),
          };
        }
        return null;
      })
      .filter((service): service is AdditionalServiceVoice => service !== null);
  }

  getRemovedServices(product: VoiceProduct): AdditionalServiceVoice[] {
    const removedAdditionalServices: AdditionalServiceVoice[] = this.getRemovedAdditionalServices(product);
    const removedMutexServices: AdditionalServiceVoice[] = this.getRemovedMutexServices(product);
    return removedAdditionalServices.concat(removedMutexServices);
  }

  private getRemovedMutexServices(product: VoiceProduct): AdditionalServiceVoice[] {
    return product.subscriptionServices.mutexServiceSets.flatMap(set => {
      const control = this.form.controls
        .filter((x: FormGroup) => x.controls.isMutexService)
        .find((x: FormGroup) => x.controls.mutexName.value === set.name) as FormGroup;

      if (!control) {
        return [];
      }

      return set.mutexServices
        .map(service => {
          const selectedService = product.additionalServices.find(s => s.productId === service.productId);
          const isMutexServiceUpdated =
            control.controls.selected.value && control.controls.selectedMutexService.value !== service.productId;

          const serviceHasBeenDeselected = !control.controls.selected.value;

          const isServiceDeselectedOrUpdated = serviceHasBeenDeselected || isMutexServiceUpdated;
          const isServiceToRemove = isServiceDeselectedOrUpdated && selectedService;

          if (isServiceToRemove) {
            return { ...service, mutexSetName: set.name };
          }
          return null;
        })
        .filter((service): service is AdditionalServiceVoice => service !== null);
    });
  }

  private getRemovedAdditionalServices(product: VoiceProduct): AdditionalServiceVoice[] {
    return product.subscriptionServices.serviceProducts
      .filter(service => !this.isGroupedSim(service))
      .map(service => {
        const control = this.form.controls
          .filter((x: FormGroup) => !x.controls.isMutexService)
          .find((x: FormGroup) => x.controls.productId.value === service.productId);

        const isSelectedInForm = (control as FormGroup)?.controls.selected.value;
        const productContainsService = product.additionalServices.some(s => s.productId === service.productId);

        if (!isSelectedInForm && productContainsService) {
          return service;
        }
        return null;
      })
      .filter((service): service is AdditionalServiceVoice => service !== null);
  }

  get hasAvailableAdditionalServices(): boolean {
    return this.form.controls.length > 0 || this.simGroups().length > 0;
  }

  get assignedVasProduct(): PcmProduct {
    const vasAddon = this.product().assignedSubscription?.addons.find(
      addon => addon.productDetails.pcmProductCategory?.value === PcmProductCategoryType.ValueAddedService
    );

    return OfferingHelperService.mapFromVasAddonToVasProduct(vasAddon);
  }

  get isRegularIceJunior(): boolean {
    return this.product().offering?.id
      ? SubscriptionHelperService.isProductIceJunior(this.product().offering.subscriptionType)
      : SubscriptionHelperService.isProductIceJunior(this.product().subscription?.subscriptionType);
  }

  getAdditionalService(item: FormGroup): AdditionalServiceVoice {
    const additionalService = this.product().subscriptionServices.serviceProducts.find(
      s => s.productId === item.controls.productId.value
    );
    return additionalService;
  }

  getMutexService(item: FormGroup): AdditionalServiceMutex {
    const mutexServiceSet = this.product().subscriptionServices.mutexServiceSets.find(
      s => s.name === item.controls.mutexName.value
    );
    return mutexServiceSet;
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }
}
